'use client';

import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useTelegramLogin } from '@/hooks/useTelegramLogin';
import { useState } from 'react';
import { useAuthPostAction } from '@/hooks/useAuthPostAction';
import { stringField } from '@/lib/jsonBody';
import { useAuthStore } from '@/stores/authStore';
import { getDeviceAccounts } from '@/utils/deviceAccounts';
import { getBotUrl } from '@/utils/telegram';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { FaSteam, FaTelegram } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { authButtonClass } from '@/screens/auth/AuthScreenShell';
import translations from '@/screens/auth/LoginScreen/LoginScreen.translations';

interface LoginMethodsProps {
  next?: string;
}

export function LoginMethods({ next }: LoginMethodsProps) {
  const { t } = useLocalTranslation(translations);
  const markAuthed = useAuthStore((s) => s.markAuthed);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { ready, requestIdToken } = useTelegramLogin(!isAuthenticated);
  const [malformedSession, setMalformedSession] = useState(false);
  const login = useAuthPostAction({
    path: '/api/auth/telegram/oidc',
    tag: 'LoginMethods',
    errorText: t('loginError'),
    onSuccess: (data) => {
      const userId = stringField(data, 'userId');
      if (userId) markAuthed(userId);
      else setMalformedSession(true);
    },
  });
  const loginError = login.error ?? (malformedSession ? t('loginError') : null);

  const handleTelegramLogin = async () => {
    login.setError(null);
    let idToken: string;
    try {
      idToken = await requestIdToken();
    } catch {
      login.setError(t('loginError'));
      return;
    }

    await login.run({ idToken, u: getDeviceAccounts() });
  };

  const handleSteamLogin = () => {
    const target = next ?? window.location.pathname;
    if (target && target !== '/' && target !== '/login') {
      sessionStorage.setItem('shot_login_next', target);
    }
    window.location.href = `${getApiBaseUrl()}/api/users/login/steam`;
  };

  return (
    <div className="w-full max-w-[320px] flex flex-col gap-3">
      <div className="w-full flex items-center justify-center min-h-[46px]">
        <Button
          variant="telegram"
          stretched
          className={authButtonClass}
          loading={login.loading}
          disabled={!ready}
          onClick={handleTelegramLogin}
        >
          <FaTelegram size={20} />
          <span>{t('loginWithTelegram')}</span>
        </Button>
      </div>

      <Button
        variant="steam"
        stretched
        className={authButtonClass}
        disabled={login.loading}
        onClick={handleSteamLogin}
      >
        <FaSteam size={20} />
        <span>{t('loginWithSteam')}</span>
      </Button>

      {loginError && <p className="text-accent-red text-sm text-center mt-2">{loginError}</p>}

      <button
        type="button"
        onClick={() => window.open(getBotUrl(), '_blank')}
        className="mt-2 text-sm text-white/50 hover:text-white/80 underline-offset-2 hover:underline transition-colors text-center"
      >
        {t('openInTelegram')}
      </button>
    </div>
  );
}
